import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tabs, Tab, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import type { EstimateFull } from '../../services/estimateService';
import type { PozycjeKosztorysStats } from '../../utils/sumNetFromEasyPrzedmiarRows';
import EasyEstimateKartaInfoTab from './EasyEstimateKartaInfoTab';
import EasyEstimatePrzedmiarTab, {
  formatPrzedmiarSelectionIloscSum,
  type PrzedmiarTableEntry,
} from './EasyEstimatePrzedmiarTab';
import EasyEstimateTesTab from './EasyEstimateTesTab';

export type EasyEstimateEstimateShellProps = {
  estimate: EstimateFull;
  onEstimateSaved: () => void;
};

function a11yProps(index: number) {
  return {
    id: `easy-estimate-tab-${index}`,
    'aria-controls': `easy-estimate-tabpanel-${index}`,
  };
}

/** Zakładki w EasyEstimate (bez modułu /app/estimates). */
const EASY_ESTIMATE_TABS = ['Karta Info', 'Kosztorys', 'TES'] as const;

/** Suma zaznaczenia (wartość netto) w downbarze — jawne grupowanie tysięcy (np. 8 820,00). */
function formatDownbarSelectionCurrencyPl(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n
    .toLocaleString('pl-PL', {
      useGrouping: true,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\u202f/g, ' ')
    .replace(/\u00a0/g, ' ');
}

/**
 * Widok kosztorysu EasyEstimate — dolny pasek (zakładki + wartość netto) i panele zakładek.
 */
const EasyEstimateEstimateShell: React.FC<EasyEstimateEstimateShellProps> = ({ estimate, onEstimateSaved }) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(1);
  /** Suma kolumny „Wartość netto” z tabeli kosztorysu (zakładka Kosztorys). */
  const [wartoscNettoZPrzedmiaru, setWartoscNettoZPrzedmiaru] = React.useState(0);
  /** Suma pionowego zaznaczenia w Ilość / Wartość netto (tylko pozycje). */
  const [numericSelectionSum, setNumericSelectionSum] = React.useState<{
    show: boolean;
    sum: number;
    selectionKind: 'quantity' | 'currency';
  }>({
    show: false,
    sum: 0,
    selectionKind: 'currency',
  });
  /** Pozycje z ceną jedn. / wszystkie — z tabeli Kosztorys (EasyEstimatePrzedmiarTab). */
  const [pozycjeStats, setPozycjeStats] = React.useState<PozycjeKosztorysStats>({ withCenaJedn: 0, total: 0 });
  /** Nagłówek + obiekt z karty (Ogólne) — widoczne w dolnym pasku; gdy Karta Info nieaktywna, z API. */
  const [kartaOgolne, setKartaOgolne] = React.useState({
    headerName: estimate.name?.trim() ?? '',
    objectName: estimate.object_name?.trim() ?? '',
    branza: estimate.branza?.trim() ?? '',
  });
  /** Roboczogodzina (zł) — live z Karty Info, także przed zapisem (mini-arkusz KNR). */
  const [roboczogodzinaZl, setRoboczogodzinaZl] = React.useState(String(estimate.rg ?? ''));
  /** Dane przedmiaru zsynchronizowane z zakładki Kosztorys — do TES bez drugiego GET. */
  const [tesPrzedmiarEntries, setTesPrzedmiarEntries] = React.useState<PrzedmiarTableEntry[]>([]);
  const [tesCellOverrides, setTesCellOverrides] = React.useState<Record<string, string>>({});
  /** Rozwinięte formuły „Cena jedn.” (np. `=F4`) — TES liczy sumy działów z tej mapy. */
  const [tesResolvedCenaJedn, setTesResolvedCenaJedn] = React.useState<Map<string, string> | undefined>(undefined);

  const handleTesPrzedmiarSourceChange = React.useCallback(
    (source: {
      entries: PrzedmiarTableEntry[];
      cellOverrides: Record<string, string>;
      resolvedCenaJednByEntryId?: Map<string, string>;
    }) => {
      setTesPrzedmiarEntries(source.entries);
      setTesCellOverrides(source.cellOverrides);
      setTesResolvedCenaJedn(source.resolvedCenaJednByEntryId);
    },
    []
  );

  React.useEffect(() => {
    setKartaOgolne({
      headerName: estimate.name?.trim() ?? '',
      objectName: estimate.object_name?.trim() ?? '',
      branza: estimate.branza?.trim() ?? '',
    });
  }, [estimate.id, estimate.name, estimate.object_name, estimate.branza]);

  React.useEffect(() => {
    setRoboczogodzinaZl(String(estimate.rg ?? ''));
  }, [estimate.id, estimate.rg]);

  React.useEffect(() => {
    setPozycjeStats({ withCenaJedn: 0, total: 0 });
  }, [estimate.id]);

  React.useEffect(() => {
    setTesPrzedmiarEntries([]);
    setTesCellOverrides({});
  }, [estimate.id]);

  React.useEffect(() => {
    setNumericSelectionSum({ show: false, sum: 0, selectionKind: 'currency' });
  }, [estimate.id]);

  const handleNumericColumnSelectionSumChange = React.useCallback(
    (payload: { show: boolean; sum: number; selectionKind: 'quantity' | 'currency' }) => {
      setNumericSelectionSum(payload);
    },
    []
  );

  const handleOgolneSummaryChange = React.useCallback(
    (v: { headerName: string; objectName: string; branza: string }) => {
      setKartaOgolne(v);
    },
    []
  );

  const handleRgChange = React.useCallback((rg: string) => {
    setRoboczogodzinaZl(rg);
  }, []);

  React.useEffect(() => {
    setTabValue((v) => (v >= EASY_ESTIMATE_TABS.length ? EASY_ESTIMATE_TABS.length - 1 : v));
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const wkr = Number.isFinite(wartoscNettoZPrzedmiaru) ? wartoscNettoZPrzedmiaru : 0;

  const goBackToOfferKosztorysy = React.useCallback(() => {
    const oid = estimate.standalone_offer_id?.trim();
    if (oid) {
      navigate(`/app/easy-estimate/offer/${encodeURIComponent(oid)}`);
    } else {
      navigate('/app/easy-estimate');
    }
  }, [estimate.standalone_offer_id, navigate]);

  return (
    <Box
      sx={{
        /** 100% — wypełnia main layoutu; 100vh psuje flex i powoduje scroll całej strony. */
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          /** Dokładnie wysokość fixed paska (40px); wcześniej 48px zostawiało zbędną szczelinę. */
          paddingBottom: '40px',
          minHeight: 0,
        }}
      >
        {/*
          Wszystkie panele zostają zamontowane (display: none gdy nieaktywne), żeby Kosztorys nie tracił
          stanu i auto-zapisu przy przełączaniu zakładek.
        */}
        <Box
          role="tabpanel"
          id="easy-estimate-tabpanel-0"
          aria-labelledby="easy-estimate-tab-0"
          hidden={tabValue !== 0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: tabValue === 0 ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <EasyEstimateKartaInfoTab
            estimate={estimate}
            onSaved={onEstimateSaved}
            onOgolneSummaryChange={handleOgolneSummaryChange}
            onRgChange={handleRgChange}
          />
        </Box>
        <Box
          role="tabpanel"
          id="easy-estimate-tabpanel-1"
          aria-labelledby="easy-estimate-tab-1"
          hidden={tabValue !== 1}
          sx={{
            flex: 1,
            minHeight: 0,
            display: tabValue === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <EasyEstimatePrzedmiarTab
            estimate={estimate}
            kosztorysTabActive={tabValue === 1}
            roboczogodzinaZl={roboczogodzinaZl}
            onWartoscNettoSumChange={setWartoscNettoZPrzedmiaru}
            onPozycjeStatsChange={setPozycjeStats}
            onEstimateSaved={onEstimateSaved}
            onTesPrzedmiarSourceChange={handleTesPrzedmiarSourceChange}
            onNumericColumnSelectionSumChange={handleNumericColumnSelectionSumChange}
          />
        </Box>
        <Box
          role="tabpanel"
          id="easy-estimate-tabpanel-2"
          aria-labelledby="easy-estimate-tab-2"
          hidden={tabValue !== 2}
          sx={{
            flex: 1,
            minHeight: 0,
            display: tabValue === 2 ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <EasyEstimateTesTab
            estimate={estimate}
            przedmiarEntries={tesPrzedmiarEntries}
            cellOverrides={tesCellOverrides}
            resolvedCenaJednByEntryId={tesResolvedCenaJedn}
            sumaNettoPozycji={wkr}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: 0,
          backgroundColor: '#d9d9d9',
          borderTop: '1px solid #c4c4c4',
          boxShadow: '0 -1px 4px rgba(0, 0, 0, 0.12)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'stretch',
          pl: 'calc(40px + 12px + 24px + 16px)',
          pr: 2,
          height: '40px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            flex: 1,
            minWidth: 0,
            gap: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'stretch', flexShrink: 0, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, px: 0.25 }}>
            <Tooltip title="Powrót do kosztorysów oferty" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Powrót do kosztorysów oferty"
                  onClick={goBackToOfferKosztorysy}
                  sx={{
                    flexShrink: 0,
                    color: '#020242',
                    p: '4px',
                    borderRadius: '4px',
                    '&:hover': { bgcolor: 'rgba(2, 2, 66, 0.06)' },
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
            </Box>
            <Box
              aria-hidden
              sx={{
                width: '1px',
                flexShrink: 0,
                alignSelf: 'stretch',
                my: 1,
                mx: 1,
                bgcolor: 'rgba(2, 2, 66, 0.18)',
              }}
            />
            <Box
              sx={{
                pl: 0.5,
                minWidth: 0,
                flex: '0 1 auto',
                maxWidth: 'min(560px, 42vw)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0,
                overflow: 'hidden',
                py: 0.25,
              }}
              aria-label="Nagłówek i obiekt kosztorysu"
            >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                maxWidth: '100%',
                lineHeight: 1.15,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#020242',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {kartaOgolne.headerName.trim() || '—'}
              </Typography>
              {kartaOgolne.branza.trim() ? (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#5F6368',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Branża: {kartaOgolne.branza.trim()}
                </Typography>
              ) : null}
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '10px',
                fontWeight: 400,
                color: '#5F6368',
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {kartaOgolne.objectName.trim() || '—'}
            </Typography>
          </Box>
          <Box
            aria-hidden
            sx={{
              width: '1px',
              flexShrink: 0,
              alignSelf: 'stretch',
              my: 1,
              mx: 1,
              bgcolor: 'rgba(2, 2, 66, 0.18)',
            }}
          />
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="EasyEstimate — zakładki kosztorysu"
            variant="standard"
            sx={{
              flexShrink: 0,
              alignSelf: 'stretch',
              minHeight: '40px',
              pl: 0.5,
              '& .MuiTabs-flexContainer': {
                alignItems: 'center',
                minHeight: '40px',
              },
              '& .MuiTab-root': {
                minHeight: '40px',
                textTransform: 'none',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                minWidth: 'auto',
                px: 1,
                py: 0,
                mx: 0.5,
                color: '#020242',
                borderBottom: 'none',
                position: 'relative',
                '&.Mui-selected': {
                  color: '#020242',
                  fontWeight: 500,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '6px',
                    left: '8px',
                    right: '8px',
                    height: '2px',
                    backgroundColor: '#020242',
                  },
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            {EASY_ESTIMATE_TABS.map((label, index) => (
              <Tab key={index} label={label} {...a11yProps(index)} />
            ))}
          </Tabs>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              flexShrink: 0,
            }}
          >
            {numericSelectionSum.show ? (
              <>
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pl: 0.5,
                    pr: 0.5,
                    fontWeight: 500,
                    color: '#020242',
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    whiteSpace: 'nowrap',
                  }}
                  aria-live="polite"
                >
                  suma:{' '}
                  {numericSelectionSum.selectionKind === 'quantity'
                    ? formatPrzedmiarSelectionIloscSum(numericSelectionSum.sum)
                    : `${formatDownbarSelectionCurrencyPl(numericSelectionSum.sum)} zł`}
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    width: '1px',
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    my: 1,
                    mx: 1,
                    bgcolor: 'rgba(2, 2, 66, 0.18)',
                  }}
                />
              </>
            ) : (
              <Box
                aria-hidden
                sx={{
                  width: '1px',
                  flexShrink: 0,
                  alignSelf: 'stretch',
                  my: 1,
                  mx: 1,
                  bgcolor: 'rgba(2, 2, 66, 0.18)',
                }}
              />
            )}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                pl: 0.5,
                fontWeight: 500,
                color: '#c53030',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif',
                whiteSpace: 'nowrap',
                textAlign: 'right',
                paddingRight: '12px',
              }}
            >
              Wartość netto:{' '}
              {wkr.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
            </Box>
            <Box
              aria-hidden
              sx={{
                width: '1px',
                flexShrink: 0,
                alignSelf: 'stretch',
                my: 1,
                mx: 1,
                bgcolor: 'rgba(2, 2, 66, 0.18)',
              }}
            />
            <Tooltip
              title={
                pozycjeStats.uniqueTotal != null && pozycjeStats.uniqueTotal < pozycjeStats.total
                  ? `Pozycje z ceną jedn. > 0 / wszystkie pozycje (w nawiasie: unikalne po „Podstawa” + „Opis”). Powtórki można szybko powiązać przyciskiem z różdżką (=F{nr}).`
                  : 'Pozycje z ceną jednostkową większą od zera / wszystkie pozycje w kosztorysie (0 zł = brak ceny)'
              }
              placement="top"
            >
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  alignSelf: 'center',
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: 'rgba(2, 2, 66, 0.08)',
                  color: '#020242',
                  border: '1px solid rgba(2, 2, 66, 0.22)',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  fontFamily: 'Roboto, sans-serif',
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
                aria-label={
                  pozycjeStats.uniqueTotal != null && pozycjeStats.uniqueTotal < pozycjeStats.total
                    ? `Pozycje z ceną jednostkową większą od zera: ${pozycjeStats.withCenaJedn} z ${pozycjeStats.total}; unikalnych pozycji po „Podstawa” + „Opis”: ${pozycjeStats.uniqueTotal}.`
                    : `Pozycje z ceną jednostkową większą od zera: ${pozycjeStats.withCenaJedn} z ${pozycjeStats.total}`
                }
              >
                {pozycjeStats.withCenaJedn}/{pozycjeStats.total}
                {pozycjeStats.uniqueTotal != null && pozycjeStats.uniqueTotal < pozycjeStats.total
                  ? `(${pozycjeStats.uniqueTotal})`
                  : ''}
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EasyEstimateEstimateShell;
